import { useSkinConfigs } from '../../hooks/useSkinConfigs';
import { SIDEBAR_OPTION, type SidebarType } from '../sidebar';
import { EnumValues, Skin, SKIN } from '../../types/enums';

export const SPORTSBOOK_HEADER_HEIGHT = 64;
export const GOLA_HEADER_HEIGHT = 46;
export const NEOBET_HEADER_HEIGHT = 72;
export const NEOBET_MOBILE_HEADER_HEIGHT = 60;
export const NEOBET_PUBLIC_MOBILE_HEADER_HEIGHT = NEOBET_MOBILE_HEADER_HEIGHT;
export const NEOBET_MOBILE_BOTTOM_NAV_HEIGHT = 61;

export const SKIN_FEATURE = {
  FAVORITES: 'favorites',
  ODDS_CHANGE_INDICATOR: 'oddsChangeIndicator',
} as const;

export type SkinFeatureKey = EnumValues<typeof SKIN_FEATURE>;
export type SkinFeatures = Record<SkinFeatureKey, boolean>;
export type InPlayTableConfig = {
  showActionColumn: boolean;
};
export type HeaderAuthButtonLabelKeys = {
  login: string;
  register: string;
};

// Shell config is the skin seam for layout behavior that spans header, drawer,
// and main canvas. Keep it declarative so shared layouts do not branch on skin.
export type ShellConfig = {
  // Only sidebar types listed here may enter compact desktop mode.
  // Drawer/header widths still live in the skin implementation.
  compactSidebarTypes: SidebarType[];
};

export type LayoutRoutes = {
  sideContentRoutes: string[];
  jackpotRoutes: string[];
  fullWidthRoutes: string[];
  betslipRoutes?: string[];
};

export type FavoriteIcon = 'star' | 'pin';
export type FavoriteIconVisibility = 'hover' | 'always';

export type SkinConfig = {
  headerHeight: number;
  headerHeightMobile?: number;
  headerHeightPublicMobile: number;
  mobileBottomNavHeight?: number;
  mobileBetslipOffset?: number;
  showCurrencyAsIso?: boolean;
  currencyPosition?: 'left' | 'right';
  features: SkinFeatures;
  favoriteIcon?: FavoriteIcon;
  favoriteIconVisibility?: FavoriteIconVisibility;
  favoriteCheckedColor?: string;
  shell?: ShellConfig;
  layout: LayoutRoutes;
  inPlayTable: InPlayTableConfig;
  headerAuthButtonLabelKeys: HeaderAuthButtonLabelKeys;
};

export const matchesLayoutRoutes = (pathname: string, routes?: string[]) => {
  return Boolean(routes?.some((route) => (route === '/' ? pathname === route : pathname.startsWith(route))));
};

export const shouldShowBetslipForPath = ({ pathname, layout }: { pathname: string; layout: LayoutRoutes }) => {
  if (!layout.betslipRoutes?.length) {
    return true;
  }
  return matchesLayoutRoutes(pathname, layout.betslipRoutes);
};

export const SKIN_CONFIGS: Partial<Record<Skin, SkinConfig>> = {
  [SKIN.BET365]: {
    headerHeight: SPORTSBOOK_HEADER_HEIGHT,
    headerHeightPublicMobile: 98,
    mobileBetslipOffset: 36,
    showCurrencyAsIso: false,
    currencyPosition: 'left',
    features: {
      favorites: true,
      oddsChangeIndicator: false,
    },
    favoriteIcon: 'star',
    favoriteIconVisibility: 'hover',
    layout: {
      sideContentRoutes: ['/sports', '/my-bets'],
      jackpotRoutes: ['/sports'],
      fullWidthRoutes: ['/casino'],
    },
    inPlayTable: {
      showActionColumn: true,
    },
    headerAuthButtonLabelKeys: {
      login: 'register.signIn',
      register: 'register.title',
    },
  },
  [SKIN.GOLA]: {
    headerHeight: GOLA_HEADER_HEIGHT,
    headerHeightPublicMobile: GOLA_HEADER_HEIGHT,
    mobileBetslipOffset: 36,
    showCurrencyAsIso: true,
    currencyPosition: 'right',
    features: {
      favorites: false,
      oddsChangeIndicator: false,
    },
    favoriteIcon: 'star',
    favoriteIconVisibility: 'hover',
    layout: {
      sideContentRoutes: [],
      jackpotRoutes: [],
      fullWidthRoutes: ['/casino'],
    },
    inPlayTable: {
      showActionColumn: false,
    },
    headerAuthButtonLabelKeys: {
      login: 'register.signIn',
      register: 'register.title',
    },
  },
  [SKIN.NEOBET]: {
    headerHeight: NEOBET_HEADER_HEIGHT,
    headerHeightMobile: NEOBET_MOBILE_HEADER_HEIGHT,
    headerHeightPublicMobile: NEOBET_PUBLIC_MOBILE_HEADER_HEIGHT,
    mobileBottomNavHeight: NEOBET_MOBILE_BOTTOM_NAV_HEIGHT,
    mobileBetslipOffset: 0,
    showCurrencyAsIso: false,
    currencyPosition: 'left',
    features: {
      favorites: true,
      oddsChangeIndicator: true,
    },
    favoriteIcon: 'pin',
    favoriteIconVisibility: 'always',
    favoriteCheckedColor: 'error.light',
    shell: {
      compactSidebarTypes: [SIDEBAR_OPTION.SPORTS, SIDEBAR_OPTION.IN_PLAY, SIDEBAR_OPTION.IN_PLAY_MATCH],
    },
    layout: {
      sideContentRoutes: [],
      jackpotRoutes: [],
      fullWidthRoutes: [
        '/',
        '/sports',
        '/in-play',
        '/my-bets',
        '/casino',
        '/promotions',
        '/search',
        '/my-account',
        '/current-balance',
        '/account-balance',
        '/user-list',
        '/user-details',
        '/change-password',
      ],
      betslipRoutes: ['/', '/sports', '/in-play', '/my-bets', '/search'],
    },
    inPlayTable: {
      showActionColumn: false,
    },
    headerAuthButtonLabelKeys: {
      login: 'login.title',
      register: 'register.register',
    },
  },
};

export const useSkinFeature = (feature: SkinFeatureKey): boolean => {
  const config = useSkinConfigs(SKIN_CONFIGS);
  return config.features[feature];
};

export const useInPlayTableConfig = (): InPlayTableConfig => {
  const config = useSkinConfigs(SKIN_CONFIGS);
  return config.inPlayTable;
};

export const useFavoriteIconConfig = (): {
  icon: FavoriteIcon;
  visibility: FavoriteIconVisibility;
  checkedColor?: string;
} => {
  const config = useSkinConfigs(SKIN_CONFIGS);
  return {
    icon: config.favoriteIcon ?? 'star',
    visibility: config.favoriteIconVisibility ?? 'hover',
    checkedColor: config.favoriteCheckedColor,
  };
};
